/**
 * Sales orders, change orders, the release, material requirements and their settings (ADR-020,
 * ADR-021). Orders live under ["tenant", slug, "sales-orders"], requirements under
 * ["tenant", slug, "requirements"] and the settings under ["tenant", slug, "order-settings"].
 * Every order action answers with the order's detail, which replaces the cached one.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { registerLiveTopic } from "@/app/live/topics";

import { api, unwrap } from "../client";
import type { components } from "../generated/schema";
import { noErrorToast } from "../mutationMeta";
import type { Body } from "./operations";
import { opsKeys } from "./operations";
import { phaseKeys } from "./phases";
import { salesKeys } from "./sales";

type S = components["schemas"];
const path = (slug: string) => ({ path: { tenant_slug: slug } });
const pathId = (slug: string, id: string) => ({ path: { tenant_slug: slug, id } });

export type SalesOrderRow = S["SalesOrderList"];
export type SalesOrder = S["SalesOrderDetail"];
export type OrderRevision = S["SalesOrderRevision"];
export type OrderLine = S["SalesOrderLine"];
export type OrderSection = S["SalesOrderSection"];
export type ReleaseCheck = S["ReleaseCheck"];
export type ReleaseQueueRow = S["ReleaseQueueRow"];
export type OrderEvent = S["SalesOrderEvent"];
export type Requirement = S["MaterialRequirement"];
export type RequirementDetail = S["MaterialRequirementDetail"];
export type PaymentTerm = S["PaymentTerm"];
export type ReleaseRule = S["ReleaseRule"];
export type ReleaseRequirement = S["ReleaseRequirementEnum"];
export type OrderCreate = Body<"/api/t/{tenant_slug}/sales-orders/", "post">;
export type OrderLineWrite = Body<"/api/t/{tenant_slug}/sales-orders/{id}/lines/", "post">;

/** A change order's impact (`revision.impact`, computed on submission; domain.services.sales_orders.impact). */
export type ChangeImpact = {
  commercial: { total_before: string; total_after: string; delta: string; subtotal_delta: string };
  lines: Array<{
    line_key: string;
    change: "added" | "removed" | "changed";
    description: string;
    quantity_before: string | null;
    quantity_after: string | null;
    unit: string;
    extended_before: string | null;
    extended_after: string | null;
    invoiced: string | null;
  }>;
  material: Array<{
    line_key: string;
    item_id: string | null;
    sku: string;
    unit: string;
    required_before: string;
    required_after: string;
    delta: string;
    reserved: string;
    consumed: string;
    on_order: string;
    surplus: string;
  }>;
  invoiced: Record<string, { amount: string; quantity: string }>;
  activities: string[];
  work_orders: string[];
};

export function impactOf(revision: OrderRevision | null | undefined): ChangeImpact | null {
  const value = revision?.impact as Partial<ChangeImpact> | null | undefined;
  return value && typeof value === "object" && value.commercial ? (value as ChangeImpact) : null;
}

export const orderKeys = {
  all: (slug: string) => ["tenant", slug, "sales-orders"] as const,
  list: (slug: string) => ["tenant", slug, "sales-orders", "list"] as const,
  detail: (slug: string, id: string) => ["tenant", slug, "sales-orders", "detail", id] as const,
  requirements: (slug: string) => ["tenant", slug, "requirements"] as const,
  settings: (slug: string) => ["tenant", slug, "order-settings"] as const,
};

registerLiveTopic("sales_order", (_e, slug) => [orderKeys.all(slug), orderKeys.requirements(slug), phaseKeys.all(slug)]);
registerLiveTopic("requirement", (_e, slug) => [orderKeys.requirements(slug)]);
registerLiveTopic("reservation", (_e, slug) => [orderKeys.requirements(slug)]);
registerLiveTopic("purchase_order", (_e, slug) => [orderKeys.requirements(slug)]);
registerLiveTopic("receiving", (_e, slug) => [orderKeys.requirements(slug)]);
registerLiveTopic("readiness", (_e, slug) => [orderKeys.all(slug)]);

// --- orders ------------------------------------------------------------------------------------------
export type OrderFilters = {
  status?: string;
  release_status?: string;
  origin?: string;
  client?: string;
  job?: string;
  phase?: string;
  salesperson?: string;
  estimate?: string;
  on_hold?: "true" | "false";
  q?: string;
  page?: number;
  page_size?: number;
};

export function useSalesOrders(slug: string, filters: OrderFilters, enabled = true) {
  return useQuery({
    queryKey: [...orderKeys.list(slug), filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/sales-orders/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useSalesOrder(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: orderKeys.detail(slug, id ?? ""),
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/sales-orders/{id}/", { params: pathId(slug, id!) })),
  });
}

export function useOrderHistory(slug: string, id: string, enabled = true) {
  return useQuery({
    queryKey: [...orderKeys.all(slug), "history", id],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/sales-orders/{id}/history/", { params: pathId(slug, id) })),
  });
}

export function useOrderAttachments(slug: string, id: string, enabled = true) {
  return useQuery({
    queryKey: [...orderKeys.all(slug), "attachments", id],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/sales-orders/{id}/attachments/", { params: pathId(slug, id) })),
  });
}

/** Sales › Release orders: confirmed orders waiting to be released, longest waiting first, each with
 * what it doesn't meet yet (25 a page). */
export function useReleaseQueue(slug: string, page: number, enabled = true) {
  return useQuery({
    queryKey: [...orderKeys.all(slug), "to-release", "list", page],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/sales-orders/to-release/", { params: { ...path(slug), query: { page } } })),
    placeholderData: keepPreviousData,
  });
}

/** How many orders wait to be released (the count beside Release orders in the menu). */
export function useReleaseQueueCount(slug: string, enabled = true) {
  return useQuery({
    queryKey: [...orderKeys.all(slug), "to-release", "count"],
    enabled,
    staleTime: 60_000,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/sales-orders/to-release/count/", { params: path(slug) })),
  });
}

/** The release rules evaluated now (fresh each time the release dialog opens). */
export function useReleaseChecks(slug: string, id: string, enabled = true) {
  return useQuery({
    queryKey: [...orderKeys.all(slug), "release-checks", id],
    enabled,
    staleTime: 0,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/sales-orders/{id}/release-checks/", { params: pathId(slug, id) })),
  });
}

function useAfterOrderChange(slug: string) {
  const qc = useQueryClient();
  return (data: SalesOrder) => {
    qc.setQueryData(orderKeys.detail(slug, data.id), data);
    void qc.invalidateQueries({ queryKey: orderKeys.list(slug) });
    void qc.invalidateQueries({ queryKey: [...orderKeys.all(slug), "history", data.id] });
    void qc.invalidateQueries({ queryKey: [...orderKeys.all(slug), "attachments", data.id] });
    void qc.invalidateQueries({ queryKey: orderKeys.requirements(slug) });
    void qc.invalidateQueries({ queryKey: phaseKeys.all(slug) });
    void qc.invalidateQueries({ queryKey: salesKeys.jobs(slug) });
    void qc.invalidateQueries({ queryKey: salesKeys.drawings(slug) });
    void qc.invalidateQueries({ queryKey: opsKeys.inventory(slug) });
  };
}

/** Create a builder PO / direct sale, or convert an accepted estimate revision. */
export function useCreateSalesOrder(slug: string) {
  const after = useAfterOrderChange(slug);
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { action: "create"; body: OrderCreate }
        | { action: "from-estimate"; body: Body<"/api/t/{tenant_slug}/sales-orders/from-estimate/", "post"> },
    ): Promise<SalesOrder> =>
      vars.action === "create"
        ? unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/", { params: path(slug), body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/from-estimate/", { params: path(slug), body: vars.body })),
    onSuccess: (data) => {
      after(data);
      void qc.invalidateQueries({ queryKey: salesKeys.estimates(slug) });
    },
  });
}

type Reason = Body<"/api/t/{tenant_slug}/sales-orders/{id}/hold/", "post">;

export type OrderAction =
  | { action: "update"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/", "patch"> }
  | { action: "add-line"; body: OrderLineWrite }
  | { action: "add-section"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/sections/", "post"> }
  | { action: "reorder"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/reorder/", "post"> }
  | { action: "revision"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/revision/", "post"> }
  | { action: "submit"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/submit/", "post"> }
  | { action: "decide"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/decide/", "post"> }
  | { action: "approval"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/approvals/", "post"> }
  | { action: "accept"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/accept/", "post"> }
  | { action: "release"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/release/", "post"> }
  | { action: "hold" | "resume" | "cancel" | "close" | "change" | "discard-change"; body: Reason }
  | { action: "line"; lineId: string; body: Body<"/api/t/{tenant_slug}/sales-order-lines/{id}/", "patch"> }
  | { action: "delete-line"; lineId: string }
  | { action: "section"; sectionId: string; body: Body<"/api/t/{tenant_slug}/sales-order-sections/{id}/", "patch"> }
  | { action: "delete-section"; sectionId: string }
  | { action: "answer"; selectionId: string; body: Body<"/api/t/{tenant_slug}/sales-order-selections/{id}/answer/", "post"> }
  | { action: "move-lines"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/move-lines/", "post"> }
  | { action: "link-job"; body: Body<"/api/t/{tenant_slug}/sales-orders/{id}/link-job/", "post"> };

export function useSalesOrderAction(slug: string, orderId: string) {
  const after = useAfterOrderChange(slug);
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: OrderAction): Promise<SalesOrder> => {
      const p = pathId(slug, orderId);
      switch (vars.action) {
        case "update":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/sales-orders/{id}/", { params: p, body: vars.body }));
        case "add-line":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/lines/", { params: p, body: vars.body }));
        case "add-section":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/sections/", { params: p, body: vars.body }));
        case "reorder":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/reorder/", { params: p, body: vars.body }));
        case "revision":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/revision/", { params: p, body: vars.body }));
        case "submit":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/submit/", { params: p, body: vars.body }));
        case "decide":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/decide/", { params: p, body: vars.body }));
        case "approval":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/approvals/", { params: p, body: vars.body }));
        case "accept":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/accept/", { params: p, body: vars.body }));
        case "release":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/release/", { params: p, body: vars.body }));
        case "hold":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/hold/", { params: p, body: vars.body }));
        case "resume":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/resume/", { params: p, body: vars.body }));
        case "cancel":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/cancel/", { params: p, body: vars.body }));
        case "close":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/close/", { params: p, body: vars.body }));
        case "change":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/change/", { params: p, body: vars.body }));
        case "discard-change":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/discard-change/", { params: p, body: vars.body }));
        case "line":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/sales-order-lines/{id}/", { params: pathId(slug, vars.lineId), body: vars.body }));
        case "delete-line":
          return unwrap(api.DELETE("/api/t/{tenant_slug}/sales-order-lines/{id}/", { params: pathId(slug, vars.lineId) }));
        case "section":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/sales-order-sections/{id}/", { params: pathId(slug, vars.sectionId), body: vars.body }));
        case "link-job":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/link-job/", { params: p, body: vars.body }));
        case "move-lines":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-orders/{id}/move-lines/", { params: p, body: vars.body }));
        case "answer":
          return unwrap(api.POST("/api/t/{tenant_slug}/sales-order-selections/{id}/answer/", { params: pathId(slug, vars.selectionId), body: vars.body }));
        default:
          return unwrap(api.DELETE("/api/t/{tenant_slug}/sales-order-sections/{id}/", { params: pathId(slug, vars.sectionId) }));
      }
    },
    onSuccess: after,
  });
}

export async function removeOrderAttachment(slug: string, orderId: string, attachmentId: string) {
  return unwrap(
    api.POST("/api/t/{tenant_slug}/sales-orders/{id}/attachments/{attachment_id}/remove/", {
      params: { path: { tenant_slug: slug, id: orderId, attachment_id: attachmentId } },
    }),
  );
}

// --- material requirements ---------------------------------------------------------------------------
export type RequirementFilters = { status?: string; sales_order?: string; phase?: string; job?: string; item?: string; shortage?: "1"; page?: number; page_size?: number };

export function useRequirements(slug: string, filters: RequirementFilters, enabled = true) {
  return useQuery({
    queryKey: [...orderKeys.requirements(slug), "list", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/requirements/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useRequirement(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...orderKeys.requirements(slug), "detail", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/requirements/{id}/", { params: pathId(slug, id!) })),
  });
}

export function useRequirementAction(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { action: "allocate"; id: string; body: Body<"/api/t/{tenant_slug}/requirements/{id}/allocate/", "post"> }
        | { action: "release"; id: string; body: Body<"/api/t/{tenant_slug}/requirements/{id}/release/", "post"> },
    ): Promise<RequirementDetail> =>
      vars.action === "allocate"
        ? unwrap(api.POST("/api/t/{tenant_slug}/requirements/{id}/allocate/", { params: pathId(slug, vars.id), body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/requirements/{id}/release/", { params: pathId(slug, vars.id), body: vars.body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orderKeys.requirements(slug) });
      void qc.invalidateQueries({ queryKey: phaseKeys.all(slug) });
      void qc.invalidateQueries({ queryKey: opsKeys.inventory(slug) });
    },
  });
}

// --- settings: payment terms and release rules ---------------------------------------------------------
export function usePaymentTerms(slug: string, enabled = true) {
  return useQuery({
    queryKey: [...orderKeys.settings(slug), "payment-terms"],
    enabled,
    staleTime: 60_000,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/payment-terms/", { params: path(slug) })),
  });
}

export function useReleaseRules(slug: string) {
  return useQuery({
    queryKey: [...orderKeys.settings(slug), "release-rules"],
    staleTime: 60_000,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/release-rules/", { params: path(slug) })),
  });
}

export function useSaveOrderSetting(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { kind: "term"; id?: string; body: Body<"/api/t/{tenant_slug}/payment-terms/", "post"> }
        | { kind: "rule"; id?: string; body: Body<"/api/t/{tenant_slug}/release-rules/", "post"> },
    ): Promise<unknown> => {
      if (vars.kind === "term")
        return vars.id
          ? unwrap(api.PATCH("/api/t/{tenant_slug}/payment-terms/{id}/", { params: pathId(slug, vars.id), body: vars.body }))
          : unwrap(api.POST("/api/t/{tenant_slug}/payment-terms/", { params: path(slug), body: vars.body }));
      return vars.id
        ? unwrap(api.PATCH("/api/t/{tenant_slug}/release-rules/{id}/", { params: pathId(slug, vars.id), body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/release-rules/", { params: path(slug), body: vars.body }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: orderKeys.settings(slug) }),
  });
}
